import type { LotLiveState } from '@skinstep/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { BidHistoryRow } from '../components/BidHistoryRow';
import { LiveStatusStrip } from '../components/LiveStatusStrip';
import { LotHero } from '../components/LotHero';
import { LotMainButton } from '../components/LotMainButton';
import type { Bidder, BidEntry, Lot } from '../data/mockLots';
import { useLotChatLink, useLotDetail } from '../hooks/useLotDetail';
import { deriveLotState, useLotState } from '../hooks/useLotState';
import { useLotWs } from '../hooks/useLotWs';
import { useSession } from '../hooks/useSession';
import { ApiError, api } from '../lib/apiClient';
import { applyLiveState, lotFromDetail, placeholderBidder } from '../lib/lots';
import { openTgLink } from '../lib/tg';

type Props = {
  lot: Lot;
  onBack: () => void;
};

const VISIBLE_BIDS = 8;
const HISTORY_LIMIT = 60;

type BidErrorCode = 'INSUFFICIENT_BALANCE' | 'EXPIRED' | 'NOT_LIVE' | 'NOT_INITIALIZED';

const BID_ERROR_COPY: Record<BidErrorCode, string> = {
  INSUFFICIENT_BALANCE: 'Not enough bids on your balance.',
  EXPIRED: 'Timer expired — this round is over.',
  NOT_LIVE: 'Lot is not live right now.',
  NOT_INITIALIZED: 'Lot is not ready yet — try again in a moment.',
};

function isBidErrorCode(code: string | undefined): code is BidErrorCode {
  return (
    code === 'INSUFFICIENT_BALANCE' ||
    code === 'EXPIRED' ||
    code === 'NOT_LIVE' ||
    code === 'NOT_INITIALIZED'
  );
}

function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function LastBidderCard({ bidder, secondsAgo }: { bidder: Bidder; secondsAgo: number }) {
  const body = (
    <div className="mx-3 mt-3 border border-edge bg-bg-elev/50 flex items-center gap-3 px-3 py-2.5">
      <div
        className="w-9 h-9 rounded-full font-mono font-semibold text-bg flex items-center justify-center shrink-0"
        style={{
          background: `linear-gradient(135deg, hsl(${bidder.hue}, 90%, 65%) 0%, hsl(${(bidder.hue + 40) % 360}, 85%, 50%) 100%)`,
          boxShadow: `0 0 0 1px hsl(${bidder.hue}, 60%, 30%), 0 0 14px hsla(${bidder.hue}, 80%, 50%, 0.35)`,
          fontSize: 14,
        }}
      >
        {bidder.initial}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="font-mono text-[9px] tracking-[0.22em] text-fg-muted">LAST BID</span>
        <span className="font-medium text-[13px] text-fg truncate">
          {bidder.firstName}
          {bidder.username && (
            <span className="text-fg-dim font-mono text-[11px] ml-1">@{bidder.username}</span>
          )}
        </span>
      </div>
      <span className="font-mono text-[10px] tabular-nums text-primary border border-primary/30 bg-primary/5 px-2 py-1 shrink-0">
        +10s · −{secondsAgo}s
      </span>
    </div>
  );

  if (bidder.username) {
    return (
      <a
        href={`https://t.me/${bidder.username}`}
        target="_blank"
        rel="noreferrer"
        className="block hover:opacity-90 transition-opacity"
      >
        {body}
      </a>
    );
  }

  return body;
}

export function LotDetail({ lot: initialLot, onBack }: Props) {
  const session = useSession();
  const queryClient = useQueryClient();
  const detailQuery = useLotDetail(initialLot.id);

  const baseLot = useMemo<Lot>(
    () => (detailQuery.data ? lotFromDetail(detailQuery.data) : initialLot),
    [detailQuery.data, initialLot],
  );

  const isLiveLot = baseLot.startsAt == null || baseLot.startsAt <= Date.now();
  const isClosedLot = deriveLotState(baseLot) === 'closed';

  const { liveState, status: wsStatus } = useLotWs(initialLot.id, {
    enabled: !isClosedLot && isLiveLot,
    onBidPlaced: (s) => appendBidFromWs(s),
    onLotClosed: () => {
      queryClient.invalidateQueries({ queryKey: ['lot', initialLot.id] });
    },
  });

  const effectiveLot = useMemo<Lot>(() => applyLiveState(baseLot, liveState), [baseLot, liveState]);

  const state = useLotState(effectiveLot);
  const closedMinutesAgo = effectiveLot.closedAt
    ? Math.max(0, Math.floor((Date.now() - effectiveLot.closedAt) / 60_000))
    : undefined;

  const chatQuery = useLotChatLink(initialLot.id, state !== 'closed');
  const chatDeepLink = chatQuery.data?.deep_link ?? null;

  const [history, setHistory] = useState<BidEntry[]>([]);
  const [flash, setFlash] = useState(0);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [bidError, setBidError] = useState<string | null>(null);

  function appendBidFromWs(s: LotLiveState) {
    setFlash((f) => f + 1);
    setHistory((h) => {
      if (s.bids_count > 0 && h.some((e) => e.bidIndex === s.bids_count)) return h;
      return [
        {
          ts: Date.now(),
          bidder: placeholderBidder(
            s.last_bidder_user_id,
            s.last_bidder_first_name,
            s.last_bidder_username,
          ),
          price: s.current_price_cents / 100,
          bidIndex: s.bids_count > 0 ? s.bids_count : undefined,
        } satisfies BidEntry,
        ...h,
      ].slice(0, HISTORY_LIMIT);
    });
  }

  useEffect(() => {
    const recent = detailQuery.data?.recent_bids;
    if (!recent || recent.length === 0) return;
    setHistory((h) => {
      const seen = new Set(h.map((e) => e.bidIndex).filter((i): i is number => i != null));
      const seeded: BidEntry[] = recent
        .filter((rb) => !seen.has(rb.bid_index))
        .map((rb) => ({
          ts: rb.placed_at,
          bidder: placeholderBidder(rb.user_id, rb.first_name, rb.username),
          price: rb.price_after_cents / 100,
          bidIndex: rb.bid_index,
        }));
      if (seeded.length === 0) return h;
      return [...h, ...seeded]
        .sort((a, b) => (b.bidIndex ?? 0) - (a.bidIndex ?? 0))
        .slice(0, HISTORY_LIMIT);
    });
  }, [detailQuery.data]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const bidMutation = useMutation({
    mutationFn: async () => {
      return await api.post<{ ok: true }>(`/api/lots/${initialLot.id}/bid`, {
        idempotency_key: makeIdempotencyKey(),
      });
    },
    onMutate: () => {
      setBidError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const copy = isBidErrorCode(err.code) ? BID_ERROR_COPY[err.code] : err.message;
        setBidError(copy);
      } else {
        setBidError('Bid failed — check your connection and retry.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const visible = useMemo(() => history.slice(0, VISIBLE_BIDS), [history]);
  const more = Math.max(0, history.length - VISIBLE_BIDS);

  const cumulativeByIndex = useMemo(() => {
    const out: number[] = new Array(history.length).fill(0);
    const tally = new Map<string, number>();
    for (let i = history.length - 1; i >= 0; i--) {
      const entry = history[i];
      if (!entry) continue;
      const c = (tally.get(entry.bidder.id) ?? 0) + 1;
      tally.set(entry.bidder.id, c);
      out[i] = c;
    }
    return out;
  }, [history]);

  const lastBidSecondsAgo = history[0]
    ? Math.max(0, Math.floor((nowTick - history[0].ts) / 1000))
    : 0;

  const balanceBids = session?.user.bid_balance ?? 0;

  return (
    <div className="min-h-dvh mx-auto max-w-[480px] relative">
      <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-edge">
        <div className="px-3 h-12 grid grid-cols-[40px_1fr_40px] items-center">
          <button
            type="button"
            onClick={onBack}
            className="w-10 h-10 -ml-2 flex items-center justify-center text-fg hover:text-primary transition-colors"
            aria-label="Back to lots"
          >
            <span className="font-mono text-[18px] leading-none">←</span>
          </button>
          <div className="flex justify-center">
            <span className="font-mono text-[11px] tracking-[0.3em] text-fg-muted">
              LOT / <span className="text-fg">{effectiveLot.code}</span>
              {wsStatus === 'connecting' && <span className="text-warning ml-2">·</span>}
              {wsStatus === 'open' && <span className="text-success ml-2">·</span>}
            </span>
          </div>
          <button
            type="button"
            className="w-10 h-10 -mr-2 flex items-center justify-center text-fg-muted hover:text-fg transition-colors"
            aria-label="Share"
          >
            <span className="font-mono text-[14px] leading-none">⇪</span>
          </button>
        </div>
      </header>

      <main className="pb-36">
        <div className="px-3 pt-3">
          <LotHero
            lot={effectiveLot}
            state={state}
            livePrice={state === 'live' ? effectiveLot.currentPrice : undefined}
            liveBids={state === 'live' ? effectiveLot.bidsCount : undefined}
            flashTrigger={flash}
            timerEndsAt={state === 'live' ? effectiveLot.timerEndsAt : undefined}
          />
        </div>

        <div className="mt-3">
          <LiveStatusStrip
            state={state}
            bidsCount={effectiveLot.bidsCount}
            bidders={effectiveLot.totalBidders}
            closedMinutesAgo={closedMinutesAgo}
          />
        </div>

        {chatDeepLink && state !== 'closed' && (
          <button
            type="button"
            onClick={() => openTgLink(chatDeepLink)}
            className="mx-3 mt-3 w-[calc(100%-1.5rem)] h-11 border border-primary/40 bg-primary/5 text-primary flex items-center justify-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-primary/10 hover:border-primary/70 active:scale-[0.99] transition-all"
          >
            <span aria-hidden>◐</span>
            <span>Lot chat</span>
            <span>↗</span>
          </button>
        )}

        {state === 'live' && history[0] && (
          <LastBidderCard bidder={history[0].bidder} secondsAgo={lastBidSecondsAgo} />
        )}

        {state === 'scheduled' && (
          <div className="mx-3 mt-4 border border-dashed border-edge px-4 py-6 text-center bg-bg-elev/30">
            <p className="font-mono text-[10px] tracking-[0.25em] text-warning">
              FIRST BID LOCKS YOUR SLOT
            </p>
            <p className="text-[13px] text-fg-muted mt-2 leading-relaxed">
              Auction opens soon. Be the first to drop a bid when the timer hits zero.
            </p>
          </div>
        )}

        {state !== 'scheduled' && visible.length > 0 && (
          <div className="mt-5">
            <div className="px-3 mb-2 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">
                BID HISTORY
              </span>
              <span className="font-mono text-[10px] text-fg-dim">{history.length} total</span>
            </div>
            <div className="flex flex-col">
              {visible.map((entry, idx) => (
                <BidHistoryRow
                  key={`${entry.ts}-${entry.bidder.id}-${entry.price}`}
                  entry={entry}
                  isLatest={idx === 0}
                  now={nowTick}
                  count={cumulativeByIndex[idx]}
                />
              ))}
            </div>
            {more > 0 && (
              <button
                type="button"
                className="w-full mt-1 py-2 font-mono text-[10px] tracking-[0.2em] text-primary hover:text-fg transition-colors"
              >
                + {more} earlier bids
              </button>
            )}
          </div>
        )}

        {state === 'live' && (
          <div className="mx-3 mt-5 border border-edge bg-bg-elev/40 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-[9px] tracking-[0.25em] text-fg-muted">BUY NOW</p>
              <p className="text-[12px] text-fg mt-1 truncate">
                Skip the auction — claim it at market.
              </p>
            </div>
            <button
              type="button"
              className="font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10 transition-colors shrink-0"
            >
              ${effectiveLot.marketPrice.toFixed(2)}
            </button>
          </div>
        )}

        {state === 'closed' && (
          <div className="mx-3 mt-5 border border-edge bg-bg-elev/40 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-[9px] tracking-[0.25em] text-fg-muted">BUY NOW</p>
              <p className="text-[12px] text-fg mt-1 truncate">
                Want this one? Pick it up at market −7%.
              </p>
            </div>
            <button
              type="button"
              className="font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10 transition-colors shrink-0"
            >
              ${(effectiveLot.marketPrice * 0.93).toFixed(2)}
            </button>
          </div>
        )}

        {bidError && (
          <div className="mx-3 mt-4 border border-danger/40 bg-danger/5 px-3 py-2 text-center">
            <p className="font-mono text-[10px] tracking-[0.22em] text-danger">{bidError}</p>
          </div>
        )}
      </main>

      <LotMainButton
        state={state}
        balanceBids={balanceBids}
        bidPriceUsd={0.1}
        winner={effectiveLot.winner}
        closedMinutesAgo={closedMinutesAgo}
        onBid={() => bidMutation.mutate()}
      />
    </div>
  );
}
